
import React, { useState, useEffect } from "react";
import "./styles.css";
import baseURL from "../../authComponents/baseURL/BaseURL";
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  ClassicEditor,
  Bold,
  Essentials,
  Heading,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  MediaEmbed,
  Paragraph,
  Table,
  Undo
} from 'ckeditor5';

import 'ckeditor5/ckeditor5.css';

const EmailsTable = () => {
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "id",
    direction: "desc",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [currentEmail, setCurrentEmail] = useState(null);
  const [editorContent, setEditorContent] = useState("");
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Get authorization token from localStorage or any other source
  const getAuthHeader = () => {
    const token = localStorage.getItem('authToken');
    return { Authorization: `Bearer ${token}` };
  };

  // Fetch emails on component mount
  useEffect(() => {
    fetchEmails();
  }, []);

  // Fetch all emails from API with authorization
  const fetchEmails = async () => {
    setLoading(true);
    try {
      const response = await baseURL.get("/admin/system_emails", {
        headers: getAuthHeader()
      });
      setEmails(response.data.emails);
      setError(null);
    } catch (err) {
      setError("Failed to fetch emails. Please try again later.");
      console.error("Error fetching emails:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch single email details with authorization
  const fetchEmailDetails = async (id) => {
    try {
      const response = await baseURL.get(`/admin/email_details/${id}`, {
        headers: getAuthHeader()
      });
      
      // Modified to work with axios response structure
      if (response.status !== 200) {
        throw new Error(`Error: ${response.status}`);
      }
      
      const emailData = response.data.email;
      setCurrentEmail(emailData);
      setEditorContent(emailData.content);
      setIsEditing(true);
    } catch (err) {
      setError("Failed to fetch email details. Please try again later.");
      console.error("Error fetching email details:", err);
    }
  };

  // Update email content on the server with authorization
  const updateEmail = async () => {
    try {
      const response = await baseURL.post("/admin/update_email_content", {
        id: currentEmail.id,
        title: currentEmail.title,
        content: editorContent,
      }, {
        headers: getAuthHeader()
      });

      if (response.status !== 200) {
        throw new Error(`Error: ${response.status}`);
      }

      // Refresh email list after successful update
      await fetchEmails();
      setIsEditing(false);
      setCurrentEmail(null);
    } catch (err) {
      setError("Failed to update email. Please try again later.");
      console.error("Error updating email:", err);
    }
  };

  // Update editorContent when currentEmail changes
  useEffect(() => {
    if (currentEmail) {
      setEditorContent(currentEmail.content);
    }
  }, [currentEmail]);

  // Sort emails based on current sort configuration
  const sortedEmails = [...emails].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === "asc" ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === "asc" ? 1 : -1;
    }
    return 0;
  });

  // Handle sort request
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Handle checkbox selection
  const handleSelect = (id) => {
    if (selectedEmails.includes(id)) {
      setSelectedEmails(selectedEmails.filter((emailId) => emailId !== id));
    } else {
      setSelectedEmails([...selectedEmails, id]);
    }
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectedEmails.length === emails.length) {
      setSelectedEmails([]);
    } else {
      setSelectedEmails(emails.map((email) => email.id));
    }
  };

  // Handle edit action
  const handleEdit = (email) => {
    fetchEmailDetails(email.id);
  };

  // Handle save
  const handleSave = () => {
    if (currentEmail) {
      updateEmail();
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setIsEditing(false);
    setCurrentEmail(null);
  };


  // Show loading state
  if (loading && emails.length === 0) {
    return (
        <div className="text-center py-10">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#730000] mx-auto"></div>
        <p className="mt-2">Loading...</p>
      </div>
    );
  }

  // Show error state
  if (error && emails.length === 0) {
    return (
      <div className="p-6 max-md:p-2 bg-gray-100 min-h-screen font-serif">
        <div className="flex justify-center items-center h-64 flex-col">
          <div className="text-lg text-red-500 mb-4">{error}</div>
          <button
            onClick={fetchEmails}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // If in editing mode, show the email editor
  if (isEditing && currentEmail) {
    return (
      <>
        <style>
        {`
          h1, h2, h3, h4, h5, h6 {
            font-family: "Times New Roman", Times, serif;
          }
        `}
      </style>
      <div
        className="p-6 max-md:p-2 bg-gray-100 min-h-screen"
        style={{ fontFamily: "Times New Roman, Times, serif" }}
      >
        <div className="bg-white rounded shadow p-6">
          <div className="mb-6">
            <h2 className="text-xl font-bold mb-4">Edit Email</h2>
            {error && <div className="mb-4 text-red-500">{error}</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={currentEmail.title}
                  onChange={(e) =>
                    setCurrentEmail({ ...currentEmail, title: e.target.value })
                  }
                  className="border rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <div className="border rounded px-3 py-2 bg-gray-50">
                  {currentEmail.mail_type}
                </div>
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Content
              </label>
              <div className="border rounded overflow-hidden" id="emailContent" style={{fontFamily: "serif"}}>
                  {
                    currentEmail != null ? <CKEditor
                    editor={ ClassicEditor }
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      setEditorContent(data)
                    }}
                    data={editorContent}
                    
                    config={ {
                      toolbar: [
                        'undo', 'redo', '|',
                        'heading', '|', 'bold', 'italic', '|',
                        'link', '|',
                        'bulletedList', 'numberedList', 'indent', 'outdent'
                      ],
                      licenseKey: "GPL",
                      plugins: [
                        Bold,
                        Essentials,
                        Heading,
                        Indent,
                        IndentBlock,
                        Italic,
                        Link,
                        List,
                        MediaEmbed,
                        Paragraph,
                        Table,
                        Undo
                      ],
                      initialData: currentEmail.content,
                    } }
                  /> : <p>Loading Data</p>
                  }
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 rounded shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 border border-transparent rounded shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>

      </>
    );
  }

  // Normal email table view
  return (
    <div className="p-6 max-md:p-2 bg-gray-100 min-h-screen font-serif">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold font-serif">Emails</h1>
        {error && <div className="text-red-500">{error}</div>}
        <div className="flex space-x-2">
          <button
            onClick={fetchEmails}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            title="Refresh list"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Email actions */}
      <div className="bg-white rounded-t shadow p-2 flex items-center space-x-4">
        <input
          type="checkbox"
          checked={selectedEmails.length === emails.length && emails.length > 0}
          onChange={handleSelectAll}
          className="h-4 w-4"
          disabled={emails.length === 0}
        />
        <div className="flex space-x-2">
          <button
            className="p-2 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={selectedEmails.length === 0}
          >
            <svg
              className="w-5 h-5 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
          <button
            className="p-2 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={selectedEmails.length === 0}
          >
            <svg
              className="w-5 h-5 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76"
              />
            </svg>
          </button>
          <button
            className="p-2 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={selectedEmails.length === 0}
          >
            <svg
              className="w-5 h-5 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Emails table */}
      <div className="bg-white rounded-b shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-[#730000] text-white font-bold">
            <tr>
              <th scope="col" className="w-10 px-3 py-3 text-left">
                <span className="sr-only">Select</span>
              </th>
              <th
                scope="col"
                className="w-16 px-3 py-3 text-left text-md font-medium uppercase tracking-wider cursor-pointer"
                onClick={() => requestSort("id")}
              >
                ID
                {sortConfig.key === "id" && (
                  <span className="ml-1">
                    {sortConfig.direction === "asc" ? "▲" : "▼"}
                  </span>
                )}
              </th>
              <th
                scope="col"
                className="px-3 py-3 text-left text-md font-medium uppercase tracking-wider cursor-pointer"
                onClick={() => requestSort("title")}
              >
                Title
                {sortConfig.key === "title" && (
                  <span className="ml-1">
                    {sortConfig.direction === "asc" ? "▲" : "▼"}
                  </span>
                )}
              </th>
              <th
                scope="col"
                className="px-3 py-3 text-left text-md font-medium uppercase tracking-wider cursor-pointer"
                onClick={() => requestSort("type")}
              >
                Type
                {sortConfig.key === "type" && (
                  <span className="ml-1">
                    {sortConfig.direction === "asc" ? "▲" : "▼"}
                  </span>
                )}
              </th>
              <th
                scope="col"
                className="px-3 py-3 text-center text-md font-medium uppercase tracking-wider"
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedEmails.length > 0 ? (
              sortedEmails.map((email) => (
                <tr
                  key={email.id}
                  className={`hover:bg-gray-50 ${
                    !email.read ? "font-semibold bg-gray-50" : ""
                  }`}
                >
                  <td className="px-3 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedEmails.includes(email.id)}
                      onChange={() => handleSelect(email.id)}
                      className="h-4 w-4"
                    />
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm">
                    {email.id}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap">{email.title}</td>
                  <td className="px-3 py-4 whitespace-nowrap text-left">
                    <span
                      className={`px-2 py-1 text-xs rounded-full`}
                    >
                      {email.mail_type}
                    </span>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-center">
                    <button
                      onClick={() => handleEdit(email)}
                      className="text-blue-600 hover:text-blue-900 font-medium text-sm"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="px-3 py-8 text-center text-gray-500">
                  No emails found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EmailsTable;